import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { FaPlus, FaTrash } from "react-icons/fa";

import {
  useGetQuizBlueprintQuery,
  useSaveQuizBlueprintMutation,
} from "../../slices/quizApiSlice";

import { useGetQuizByIdQuery } from "../../slices/quizApiSlice";

import { useGetQuestionAvailabilityByChapterQuery } from "../../slices/questionApiSlice";

import { useGetChaptersBySubjectQuery } from "../../slices/chapterApiSlice";

const emptySection = {
  name: "",
  chapter: "",
  chapterName: "",
  difficulty: "medium",
  questionType: "mcq",
  numberOfQuestions: 5,
  marksPerQuestion: 1,
};

const QuestionAvailability = ({
  index,
  chapter,
  difficulty,
  setAvailability,
}) => {
  const { data, isFetching } = useGetQuestionAvailabilityByChapterQuery(
    {
      chapter,
      difficulty,
    },
    {
      skip: !chapter || !difficulty,
    },
  );

  useEffect(() => {
    if (data?.availableQuestions !== undefined) {
      setAvailability((prev) => ({
        ...prev,
        [index]: data.availableQuestions,
      }));
    }
  }, [data, index, setAvailability]);

  if (!chapter) return null;

  return (
    <p className="mt-2 text-sm font-semibold text-blue-700">
      {isFetching
        ? "Checking availability..."
        : `Available Questions: ${data?.availableQuestions ?? 0}`}
    </p>
  );
};

const ChapterDropdown = ({ subjectId, value, onChange }) => {
  const { data, isLoading } = useGetChaptersBySubjectQuery(subjectId, {
    skip: !subjectId,
  });

  const chapters = data?.data || data || [];

  return (
    <select
      value={value}
      onChange={onChange}
      disabled={!subjectId || isLoading}
      className="w-full rounded-lg border px-4 py-2"
    >
      <option value="">Select Chapter</option>

      {chapters.map((chapter) => (
        <option key={chapter._id} value={chapter._id}>
          {chapter.name}
        </option>
      ))}
    </select>
  );
};

const EditQuizBlueprintForm = ({ quizId, onSuccess }) => {
  const { data: quiz } = useGetQuizByIdQuery(quizId);

  const {
    data: blueprintResponse,
    isLoading,
    error,
  } = useGetQuizBlueprintQuery(quizId);

  const [saveQuizBlueprint, { isLoading: saving }] =
    useSaveQuizBlueprintMutation();

  const [sections, setSections] = useState([]);

  const [availability, setAvailability] = useState({});

  const subjectId = quiz?.questionBankSubject?._id || "";

  useEffect(() => {
    const existingSections = blueprintResponse?.data?.sections || [];

    if (existingSections.length) {
      setSections(
        existingSections.map((section) => ({
          name: section.name || "",

          chapter: section.chapter?._id || section.chapter || "",

          chapterName: section.chapter?.name || "",

          difficulty: section.difficulty || "medium",

          questionType: section.questionType || "mcq",

          numberOfQuestions: section.numberOfQuestions || 1,

          marksPerQuestion: section.marksPerQuestion || 1,
        })),
      );
    }
  }, [blueprintResponse]);

  const updateSection = (index, field, value) => {
    const updated = [...sections];

    updated[index][field] = value;

    setSections(updated);
  };

  const addSection = () => {
    setSections((prev) => [...prev, { ...emptySection }]);
  };

  const removeSection = (index) => {
    if (sections.length === 1) return;

    setSections((prev) => prev.filter((_, i) => i !== index));
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    try {
      await saveQuizBlueprint({
        quizId,

        blueprint: sections.map((section) => ({
          name: section.name,

          chapter: section.chapter,

          difficulty: section.difficulty,

          questionType: section.questionType,

          numberOfQuestions: section.numberOfQuestions,

          marksPerQuestion: section.marksPerQuestion,
        })),
      }).unwrap();

      toast.success("Blueprint updated successfully");

      onSuccess?.();
    } catch (error) {
      toast.error(error?.data?.message || "Unable to update blueprint");
    }
  };

  if (isLoading) {
    return <div className="p-5 text-center">Loading blueprint...</div>;
  }

  if (error) {
    return <div className="p-5 text-red-600">Unable to load blueprint</div>;
  }

  return (
    <form
      onSubmit={submitHandler}
      className="space-y-6 rounded-3xl bg-white p-8 shadow-xl"
    >
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Edit Quiz Blueprint</h2>

        <button
          type="button"
          onClick={addSection}
          className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white"
        >
          <FaPlus />
          Add Section
        </button>
      </div>

      {sections.map((section, index) => (
        <div key={index} className="rounded-xl border bg-gray-50 p-5 space-y-4">
          <div className="flex justify-between">
            <h3 className="font-semibold">Section {index + 1}</h3>

            <button
              type="button"
              onClick={() => removeSection(index)}
              className="text-red-600"
            >
              <FaTrash />
            </button>
          </div>

          <input
            value={section.name}
            onChange={(e) => updateSection(index, "name", e.target.value)}
            placeholder="Section name"
            className="w-full rounded-lg border px-4 py-2"
          />

          <input
            value={quiz?.questionBankSubject?.name || ""}
            disabled
            className="w-full rounded-lg border bg-gray-100 px-4 py-2"
          />

          <ChapterDropdown
            subjectId={subjectId}
            value={section.chapter}
            onChange={(e) => updateSection(index, "chapter", e.target.value)}
          />

          <QuestionAvailability
            index={index}
            chapter={section.chapter}
            difficulty={section.difficulty}
            setAvailability={setAvailability}
          />

          <select
            value={section.difficulty}
            onChange={(e) => updateSection(index, "difficulty", e.target.value)}
            className="w-full rounded-lg border px-4 py-2"
          >
            <option value="easy">Easy</option>

            <option value="medium">Medium</option>

            <option value="hard">Hard</option>
          </select>

          <select
            value={section.questionType}
            onChange={(e) =>
              updateSection(index, "questionType", e.target.value)
            }
            className="w-full rounded-lg border px-4 py-2"
          >
            <option value="mcq">MCQ</option>

            <option value="true_false">True / False</option>

            <option value="multiple">Multiple Correct</option>
          </select>

          <input
            type="number"
            min="1"
            value={section.numberOfQuestions}
            onChange={(e) =>
              updateSection(index, "numberOfQuestions", Number(e.target.value))
            }
            className="w-full rounded-lg border px-4 py-2"
          />

          <input
            type="number"
            min="1"
            value={section.marksPerQuestion}
            onChange={(e) =>
              updateSection(index, "marksPerQuestion", Number(e.target.value))
            }
            className="w-full rounded-lg border px-4 py-2"
          />
        </div>
      ))}

      <button
        disabled={saving}
        className="w-full rounded-xl bg-blue-600 py-3 text-white"
      >
        {saving ? "Updating..." : "Update Blueprint"}
      </button>
    </form>
  );
};

EditQuizBlueprintForm.propTypes = {
  quizId: PropTypes.string.isRequired,

  onSuccess: PropTypes.func,
};

export default EditQuizBlueprintForm;
